import { FC, useEffect, useState } from "react";
import { Accordion } from "react-bootstrap";

import "./GroupedTrackingHistory.scss";
import { ReactComponent as ChevronDownIcon } from "assets/images/iconsV2/chevron-down.svg";
import { ReactComponent as ChevronUpIcon } from "assets/images/iconsV2/chevron-up.svg";
import { ReactComponent as MarkerPinIcon } from "assets/images/iconsV2/marker-pin-01.svg";
import Badge from "components/v2/Badge/Badge";
import TrackingHistory from "../TrackingHistory/TrackingHistory";
import { TrackingHistoryItemProps } from "../TrackingHistoryItem/TrackingHistoryItem";
// eslint-disable-next-line import/no-cycle
import { getClasses } from "./GroupedTrackingHistoryUtils";
import TrackingException from "../TrackingException/TrackingException";

export type GroupedTrackingHistoryItem = {
  /**
   * The name of the group.
   */
  groupName: string;
  /**
   * The badge text of the group.
   */
  badgeText?: string;
  /**
   * The subtitle of the group.
   */
  groupSubtitle?: string;
  /**
   * The tracking history of the group.
   */
  history: TrackingHistoryItemProps[];
  /**
   * One of the item in this group has exceptions.
   */
  hasException?: boolean;
};

type GroupedTrackingHistoryProps = {
  history: GroupedTrackingHistoryItem[];
};

const GroupedTrackingHistory: FC<GroupedTrackingHistoryProps> = function (props) {
  const { history } = props;

  const [activeAccordion, setActiveAccordion] = useState<string | null>(null);

  // Expand the last item by default
  useEffect(() => {
    if (history.length > 0) setActiveAccordion(history[history.length - 1].groupName + (history.length - 1));
  }, [history]);

  return (
    <Accordion onSelect={setActiveAccordion} activeKey={activeAccordion || ""}>
      {history.map(({ groupName, badgeText, groupSubtitle, hasException, history: trackingHistoryItems }, index) => {
        const { lineClasses, reverseLineClasses, markerIconClasses, trackingHistoryClasses, isExpanded } = getClasses(
          index,
          trackingHistoryItems,
          history,
          activeAccordion,
        );

        return (
          <div className="grouped-tracking-history__group" key={index}>
            <div className={lineClasses} />
            <div className={reverseLineClasses} />
            <Accordion.Toggle as="div" className="grouped-tracking-history__group-header" eventKey={groupName + index}>
              <div className={groupSubtitle ? "mt-1" : "d-flex align-items-center"}>
                <MarkerPinIcon className={markerIconClasses} />
              </div>
              <div className="grouped-tracking-history__group-info-container">
                <div className="grouped-tracking-history__group-name">
                  {groupName}{" "}
                  {badgeText && (
                    <span className="pl-sm">
                      <Badge label={badgeText} color="gray" type="category-badge" size="sm" />
                    </span>
                  )}
                  {hasException && (
                    <span className="ml-sm">
                      <TrackingException exceptionLabel="Exception" />
                    </span>
                  )}
                </div>
                <div className="grouped-tracking-history__group-subtitle">{groupSubtitle}</div>
              </div>
              <div className="grouped-tracking-history__chevron-icon">
                {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
              </div>
            </Accordion.Toggle>
            <Accordion.Collapse eventKey={groupName + index}>
              <div className={trackingHistoryClasses}>
                <TrackingHistory history={trackingHistoryItems} />
              </div>
            </Accordion.Collapse>
          </div>
        );
      })}
    </Accordion>
  );
};

export default GroupedTrackingHistory;
